// src/pages/AgendaPage.js
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import api from 'services/api';
import { AccessibilityContext } from 'contexts/AccessibilityContext';

import EventFilter from 'components/calendar/EventFilter';
import EventModal from 'components/calendar/EventModal';
import DayEventsModal from 'components/calendar/DayEventsModal';
import RenderEventContent from 'components/calendar/RenderEventContent';
import ListView from 'components/calendar/ListView';
import GridView from 'components/calendar/GridView';
import BannerWithSocialIcons from 'components/common/BannerWithSocialIcons';
import Breadcrumbs from 'components/common/Breadcrumbs';
import MapCta from 'components/common/MapCta';
import LiensPratiques from 'components/common/LiensPratiques';
import Spinner from 'components/common/Spinner';

import styles from './AgendaPage.module.css';
import archiveStyle from 'styles/modules/Archives.module.css';

import useDesignSettingsData from 'hooks/useDesignSettingsData';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { CalendarOutline, Grid, List } from 'react-ionicons';
import expandEventsForCalendar from 'utils/expandEventsForCalendar';
import frLocale from '@fullcalendar/core/locales/fr';

const GRID_INITIAL_LIMIT = 5; // 1 featured + 4 cards
const GRID_PAGE_SIZE = 4;
const LIST_PAGE_SIZE = 10;

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const buildFilterParams = (debouncedNameFilter, categoryFilter, specialFilter) => ({
  nameFilter: debouncedNameFilter.length >= 3 ? debouncedNameFilter : undefined,
  categoryFilter: categoryFilter || undefined,
  specialFilter: specialFilter ? 'true' : undefined,
});

const isAbortError = (err) =>
  err?.code === 'ERR_CANCELED' ||
  err?.name === 'CanceledError' ||
  err?.name === 'AbortError';

const toDateParam = (value) => {
  if (!value) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return undefined;
};

const AgendaPage = () => {
  const [nameFilter, setNameFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [specialFilter, setSpecialFilter] = useState(false);
  const debouncedNameFilter = useDebounce(nameFilter, 300);

  const [events, setEvents] = useState([]);
  const [gridEvents, setGridEvents] = useState([]);
  const [listEvents, setListEvents] = useState([]);
  const [gridTotal, setGridTotal] = useState(0);
  const [listTotal, setListTotal] = useState(0);
  const [listPage, setListPage] = useState(1);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('calendar');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [dayModalDate, setDayModalDate] = useState(null);
  const [dayModalEvents, setDayModalEvents] = useState([]);
  const [calendarRange, setCalendarRange] = useState(null);

  const calendarRef = useRef(null);
  const calendarRangeKeyRef = useRef('');
  const eventDetailAbortRef = useRef(null);

  const { subSiteSlug: routeSubSiteSlug } = useParams();
  const { getAccessibilityVariant } = useContext(AccessibilityContext);
  const { designSettings, settingsLoading, error: designError } = useDesignSettingsData();
  // Uniquement le paramètre de route `/:subSiteSlug/agenda`.
  // Le contexte sous-site prend le 1er segment d'URL : sur `/agenda` il vaut
  // "agenda" et déclenche un 404 `/subsites/agenda/...` au changement de page.
  const effectiveSubSiteSlug = routeSubSiteSlug || null;

  const refreshCalendarLayout = () => {
    const calendarApi = calendarRef.current?.getApi?.();
    if (!calendarApi) {
      return;
    }
    calendarApi.updateSize();
  };

  const getBaseUrls = useCallback(() => {
    if (effectiveSubSiteSlug) {
      return {
        calendar: `/subsites/${effectiveSubSiteSlug}/events/calendar`,
        grid: `/subsites/${effectiveSubSiteSlug}/events/calendar/grid`,
        list: `/subsites/${effectiveSubSiteSlug}/events/calendar/list`,
      };
    }
    return {
      calendar: '/events/calendar',
      grid: '/events/calendar/grid',
      list: '/events/calendar/list',
    };
  }, [effectiveSubSiteSlug]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/events/calendar/categories');
        setCategories(response.data);
      } catch (err) {
        console.error('Erreur lors de la récupération des catégories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Calendrier : fetch uniquement pour l'onglet actif + fenêtre visible
  useEffect(() => {
    if (activeTab !== 'calendar' || !calendarRange?.from || !calendarRange?.to) {
      return;
    }

    const controller = new AbortController();

    const fetchCalendarEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const { calendar } = getBaseUrls();
        const params = {
          ...buildFilterParams(debouncedNameFilter, categoryFilter, specialFilter),
          from: calendarRange.from,
          to: calendarRange.to,
        };
        const response = await api.get(calendar, {
          params,
          signal: controller.signal,
        });
        if (controller.signal.aborted) {
          return;
        }
        setEvents(expandEventsForCalendar(response.data));
      } catch (err) {
        if (isAbortError(err) || controller.signal.aborted) {
          return;
        }
        console.error('Erreur fetch calendar:', err);
        setError('Une erreur est survenue lors du chargement des événements.');
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchCalendarEvents();

    return () => controller.abort();
  }, [
    activeTab,
    calendarRange,
    debouncedNameFilter,
    categoryFilter,
    specialFilter,
    getBaseUrls,
  ]);

  // Grille : première page
  useEffect(() => {
    if (activeTab !== 'grid') {
      return;
    }

    const controller = new AbortController();

    const fetchGrid = async () => {
      setLoading(true);
      setError(null);
      try {
        const { grid } = getBaseUrls();
        const params = {
          ...buildFilterParams(debouncedNameFilter, categoryFilter, specialFilter),
          page: 1,
          limit: GRID_INITIAL_LIMIT,
        };
        const response = await api.get(grid, {
          params,
          signal: controller.signal,
        });
        if (controller.signal.aborted) {
          return;
        }
        const items = response.data?.items || [];
        setGridEvents(items);
        setGridTotal(response.data?.total || 0);
      } catch (err) {
        if (isAbortError(err) || controller.signal.aborted) {
          return;
        }
        console.error('Erreur fetch grid:', err);
        setError('Une erreur est survenue lors du chargement des événements.');
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchGrid();

    return () => controller.abort();
  }, [
    activeTab,
    debouncedNameFilter,
    categoryFilter,
    specialFilter,
    getBaseUrls,
  ]);

  // Liste : première page
  useEffect(() => {
    if (activeTab !== 'list') {
      return;
    }

    const controller = new AbortController();

    const fetchList = async () => {
      setLoading(true);
      setError(null);
      try {
        const { list } = getBaseUrls();
        const params = {
          ...buildFilterParams(debouncedNameFilter, categoryFilter, specialFilter),
          page: 1,
          limit: LIST_PAGE_SIZE,
        };
        const response = await api.get(list, {
          params,
          signal: controller.signal,
        });
        if (controller.signal.aborted) {
          return;
        }
        const items = response.data?.items || [];
        setListEvents(items);
        setListTotal(response.data?.total || 0);
        setListPage(1);
      } catch (err) {
        if (isAbortError(err) || controller.signal.aborted) {
          return;
        }
        console.error('Erreur fetch list:', err);
        setError('Une erreur est survenue lors du chargement des événements.');
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchList();

    return () => controller.abort();
  }, [
    activeTab,
    debouncedNameFilter,
    categoryFilter,
    specialFilter,
    getBaseUrls,
  ]);

  useEffect(() => {
    if (activeTab !== 'calendar' || loading || settingsLoading) {
      return;
    }

    const runRefresh = () => {
      window.requestAnimationFrame(() => {
        refreshCalendarLayout();
      });
    };

    runRefresh();
    const t1 = window.setTimeout(runRefresh, 100);
    const t2 = window.setTimeout(runRefresh, 350);

    const onResize = () => runRefresh();
    window.addEventListener('resize', onResize);

    if (document.fonts?.ready) {
      document.fonts.ready.then(runRefresh).catch(() => {});
    }

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('resize', onResize);
    };
  }, [activeTab, loading, settingsLoading, events.length]);

  const resetFilters = () => {
    setNameFilter('');
    setCategoryFilter('');
    setSpecialFilter(false);
  };

  const handleDatesSet = (dateInfo) => {
    const from = toDateParam(dateInfo.start) || toDateParam(dateInfo.startStr);
    const to = toDateParam(dateInfo.end) || toDateParam(dateInfo.endStr);
    if (!from || !to) {
      return;
    }
    const key = `${from}|${to}`;
    if (calendarRangeKeyRef.current === key) {
      return;
    }
    calendarRangeKeyRef.current = key;
    setCalendarRange({ from, to });
  };

  const handleLoadMoreGrid = async () => {
    if (loadingMore) {
      return;
    }
    setLoadingMore(true);
    try {
      const { grid } = getBaseUrls();
      const params = {
        ...buildFilterParams(debouncedNameFilter, categoryFilter, specialFilter),
        offset: gridEvents.length,
        limit: GRID_PAGE_SIZE,
      };
      const response = await api.get(grid, { params });
      const items = response.data?.items || [];
      setGridEvents((prev) => [...prev, ...items]);
      setGridTotal(response.data?.total || gridTotal);
    } catch (err) {
      console.error('Erreur load more grid:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLoadMoreList = async () => {
    if (loadingMore) {
      return;
    }
    setLoadingMore(true);
    try {
      const { list } = getBaseUrls();
      const nextPage = listPage + 1;
      const params = {
        ...buildFilterParams(debouncedNameFilter, categoryFilter, specialFilter),
        page: nextPage,
        limit: LIST_PAGE_SIZE,
      };
      const response = await api.get(list, { params });
      const items = response.data?.items || [];
      setListEvents((prev) => [...prev, ...items]);
      setListTotal(response.data?.total || listTotal);
      setListPage(nextPage);
    } catch (err) {
      console.error('Erreur load more list:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleEventClick = async (eventData) => {
    const event = eventData.event || eventData;
    const props = event.extendedProps || {};
    // extendedProps.slug vient de l'API ; ne pas utiliser event.id (id FullCalendar type "42-2025-10-08")
    const slug = props.slug || event.slug;

    // Annule une requête de détail encore en cours (évite d'afficher un autre événement)
    eventDetailAbortRef.current?.abort();
    const controller = new AbortController();
    eventDetailAbortRef.current = controller;

    // Ouvre immédiatement avec les infos déjà connues (calendrier minimal)
    setSelectedEvent({
      title: event.title || 'Titre non spécifié',
      start: props.originalStart || event.startStr || event.start || null,
      end: props.originalEnd || event.endStr || event.end || null,
      location: props.location || event.location || null,
      excerpt: props.excerpt || event.excerpt || null,
      content: [],
      categories: props.categories || event.categories || [],
      thumbnail: props.thumbnail || event.thumbnail || null,
      slug,
      id: event.id,
    });
    setIsModalOpen(true);

    if (!slug) {
      return;
    }

    try {
      const response = await api.get(`/events/read/${slug}`, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) {
        return;
      }
      const detail = Array.isArray(response.data)
        ? response.data[0]
        : response.data;
      if (detail) {
        setSelectedEvent(detail);
      }
    } catch (err) {
      if (isAbortError(err) || controller.signal.aborted) {
        return;
      }
      console.error('Erreur fetch détail événement:', err);
    }
  };

  const handleMoreLinkClick = (info) => {
    const unique = [];
    const seen = new Set();

    (info.allSegs || []).forEach((seg) => {
      const fcEvent = seg.event;
      const props = fcEvent.extendedProps || {};
      const slug = props.slug;
      const key = String(slug || fcEvent.id);
      if (!slug || seen.has(key)) {
        return;
      }
      seen.add(key);
      unique.push({
        id: fcEvent.id,
        title: fcEvent.title,
        slug,
        categories: props.categories || [],
        start: props.originalStart || seg.start,
        end: props.originalEnd || seg.end,
        location: props.location || null,
        excerpt: props.excerpt || null,
        thumbnail: props.thumbnail || null,
      });
    });

    setDayModalDate(info.date);
    setDayModalEvents(unique);
    setIsDayModalOpen(true);
    return true;
  };

  const handleDayModalEventClick = (event) => {
    setIsDayModalOpen(false);
    handleEventClick(event);
  };

  const gridHasMore = gridEvents.length < gridTotal;
  const listHasMore = listEvents.length < listTotal;

  return (
    <>
      <Helmet>
        <title>Arras | Agenda</title>
        <meta name="title" content="Arras | Agenda" />
        <meta
          name="description"
          content="Sports, culture, participation citoyenne, … Ça bouge à Arras !"
        />
        <meta name="og:title" content="Arras | Agenda" />
        <meta
          name="og:description"
          content="Sports, culture, participation citoyenne, … Ça bouge à Arras !"
        />
        <link rel="canonical" href="https://www.arras.fr/agenda" />
      </Helmet>

      {settingsLoading ? (
        <Spinner />
      ) : (
        <div
          className={`${styles.agendaPage} ${archiveStyle.pageWrapper} ${styles[getAccessibilityVariant()]}`}
        >
          <BannerWithSocialIcons
            imageUrl={`${designSettings.defaultEventBanner}`}
            altText="Bannière de la page agenda"
          />

          <section className={`global-section ${archiveStyle.headerSection}`}>
            <div className={`global-container ${archiveStyle.header}`}>
              <div className={archiveStyle.headingBlock}>
                <h1>Agenda</h1>
                <Breadcrumbs pageType="isCalendar" title="Agenda" />
              </div>
              <EventFilter
                nameFilter={nameFilter}
                setNameFilter={setNameFilter}
                categoryFilter={categoryFilter}
                setCategoryFilter={setCategoryFilter}
                resetFilters={resetFilters}
                specialFilter={specialFilter}
                setSpecialFilter={setSpecialFilter}
                categories={categories}
              />
            </div>
          </section>

          <div className={`page-content ${archiveStyle.pageContent}`}>
            {(error || designError) && (
              <div className="global-container" role="alert">
                {error || designError}
              </div>
            )}
            {loading && activeTab !== 'calendar' ? (
              <Spinner />
            ) : (
              <section className={`global-section ${archiveStyle.gridSection}`}>
                <div className="global-container">
                  <div className={archiveStyle.toggles}>
                    <button
                      className={`${archiveStyle.toggle} ${
                        activeTab === 'calendar' ? archiveStyle.active : ''
                      }`}
                      onClick={() => setActiveTab('calendar')}
                      aria-label="Bascule vue en calendrier"
                      title="Bascule vue en calendrier"
                    >
                      <CalendarOutline color="currentColor" />
                      Vue en calendrier
                    </button>

                    <button
                      className={`${archiveStyle.toggle} ${
                        activeTab === 'grid' ? archiveStyle.active : ''
                      }`}
                      onClick={() => setActiveTab('grid')}
                      aria-label="Bascule vue en grille"
                      title="Bascule vue en grille"
                    >
                      <Grid color="currentColor" />
                      Vue en grille
                    </button>

                    <button
                      className={`${archiveStyle.toggle} ${
                        activeTab === 'list' ? archiveStyle.active : ''
                      }`}
                      onClick={() => setActiveTab('list')}
                      aria-label="Bascule vue en liste"
                      title="Bascule vue en liste"
                    >
                      <List color="currentColor" />
                      Vue en liste
                    </button>
                  </div>

                  <div className={archiveStyle.dropdownContainer}>
                    <select
                      className={archiveStyle.viewSelect}
                      value={activeTab}
                      onChange={(e) => setActiveTab(e.target.value)}
                      aria-label="Sélectionnez un type de vue"
                      title="Sélectionnez un type de vue"
                    >
                      <option value="calendar">Vue en calendrier</option>
                      <option value="grid">Vue en grille</option>
                      <option value="list">Vue en liste</option>
                    </select>
                    <FontAwesomeIcon icon={faCaretDown} />
                  </div>
                </div>

                {activeTab === 'calendar' && (
                  <div className="global-container fcContainer">
                    {loading && <Spinner />}
                    <FullCalendar
                      ref={calendarRef}
                      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                      initialView="dayGridMonth"
                      slotMinTime="07:00"
                      locale={frLocale}
                      events={events}
                      eventDisplay="list-item"
                      displayEventTime={false}
                      moreLinkClick={handleMoreLinkClick}
                      height="auto"
                      datesSet={handleDatesSet}
                      eventContent={RenderEventContent}
                      eventClick={handleEventClick}
                      headerToolbar={{
                        left: 'title prev,next',
                        center: 'today',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay',
                      }}
                      buttonText={{
                        today: "Aujourd'hui",
                        month: 'Mois',
                        week: 'Semaine',
                        day: 'Jour',
                      }}
                      dayMaxEventRows={7}
                    />
                  </div>
                )}

                {activeTab === 'grid' && (
                  <GridView
                    events={gridEvents}
                    onLoadMore={handleLoadMoreGrid}
                    hasMore={gridHasMore}
                    loadingMore={loadingMore}
                  />
                )}

                {activeTab === 'list' && (
                  <ListView
                    events={listEvents}
                    onLoadMore={handleLoadMoreList}
                    hasMore={listHasMore}
                    loadingMore={loadingMore}
                  />
                )}

                {selectedEvent && (
                  <EventModal
                    key={selectedEvent.slug || selectedEvent.id}
                    isOpen={isModalOpen}
                    onRequestClose={() => setIsModalOpen(false)}
                    event={selectedEvent}
                    accessibilityVariant={getAccessibilityVariant()}
                  />
                )}
                <DayEventsModal
                  isOpen={isDayModalOpen}
                  onRequestClose={() => setIsDayModalOpen(false)}
                  date={dayModalDate}
                  events={dayModalEvents}
                  onEventClick={handleDayModalEventClick}
                />
              </section>
            )}

            <MapCta />
            <LiensPratiques />
          </div>
        </div>
      )}
    </>
  );
};

export default AgendaPage;
